import userInfo from "fingerprint-oss";
import { Fingerprint } from "../interfaces/browser";
import StorageService from "../services/storage-service";
import logger from "./logger";

interface GetFingerprint {
  onError?: (error: Error) => void;
}

type BigDataCloudReverseGeocodingData = {
  countryName?: string;
  countryCode?: string;
  city?: string;
  locality?: string;
  localityInfo?: {
    informative: [{ name?: string; description?: string; }]
  }
};

type FingerprintOSSVpnStatus = {
  vpn: {
    status: boolean;     
    probability: number; 
  }
};

export async function getFingerprint(params?: GetFingerprint): Promise<Fingerprint | null> { 
  const storageKey = "x-metrics";
  const cachedFingerprint: Fingerprint | null = StorageService.getItem(storageKey);

  if(cachedFingerprint) {
    return cachedFingerprint;
  }

  const { onError } = params ?? {};
  let fingerprint: Fingerprint | null = null;

  try { 
    // FingerprintOSS and other fingerprinting libraries use IP Address to detect location.
    // The problem with this is that the IP Address usually tracks to the user's WI-FI's ISP provided IP address, 
    // not the user's actual IP address. So, we can get Lagos, while the user is actually in Abuja.
    // By using navigator.geolocation API (which the user must explicitly accept) along with reverse geocoding, 
    // we can get the user's actual location in terms of their city, locality (and country).
    // We need that city and locality info for showing the users products from vendors within the same city/locality.
    // If the user does not explicitly consent to this or when they change city, we have to present them with an input 
    // field to type in their city or to enter a list of cities from which they wish to see vendors' products.
    const [fingerprintOSSData, bigDataCloudGPSData] = await Promise.all([
      userInfo({
        transparency: process.env.NODE_ENV === "development", 
        message: "We use this data to improve your experience"  // GDPR notice
      }),
      getUserLocationViaGPS(),
    ]);

    const { geolocation, hash, systemInfo } = fingerprintOSSData;
    const { ip, city, region, country, location, vpnStatus } = geolocation ?? {};
    const { os, browser, vendor, platform, userAgent, screenResolution, timezone, bot } = systemInfo;
    const { countryName, countryCode, city: gpsCity, locality, localityInfo } = ( 
      (bigDataCloudGPSData ?? {}) as BigDataCloudReverseGeocodingData
    );

    const geolocationTimezone = location?.timeZone;
    const systemTimezone = timezone;

    // Order of location accuracy:
    // 1. (parsedData.)location.gps is more accurate, if available, as it uses GPS information.
    //    With it, we can get a user's exact location and show them relevant products.
    // 2. (parsedData.)client.timezone is less accurate but gets us close enough 
    //    (at least for fingerprinting purposes) via the OS's timezone information. 
    // 3. (parsedData.)location.ipAddress is the least accurate, 
    //    because it uses the ISP/Wi-Fi provided IP Address, rather than the user's actual IP address. 
    //    It is especially less reliable if the user is behind a VPN (client.isUsingVpn?.evaluation === true). 
    //    In both cases, we'll get either 
    //    - the ISP's address and location info or 
    //    - the VPN's ip address and location info, 
    //    rather than the user's actual ip address and location info.
    const parsedData = { 
      client: { 
        os, 
        hash, 
        platform, 
        userAgent, 
        screenResolution, 
        timezone: systemTimezone,
        browser: { ...browser, vendor }, 
        isBot: { 
          evaluation: bot.isBot,
          probability: bot.confidence,
        },
        isUsingVpn: {
          evaluation: (vpnStatus as unknown as FingerprintOSSVpnStatus)?.vpn?.status,
          probability: (vpnStatus as unknown as FingerprintOSSVpnStatus)?.vpn?.probability
        }
      }, 
      location: { 
        ipAddress: ip as string|undefined, 
        ip: { // location info obtained via IP Address (using Fingerprint-OSS)
          region, 
          country, 
          city: { name: city },
          timezone: geolocationTimezone, 
        },
        gps: { // Location info obtained via GPS (using navigator.geolocation with BigDataClout.com's free API)
          city     : { name: gpsCity },
          locality : { name: locality },
          country  : { name: countryName, code: countryCode },
          timezone : localityInfo?.informative?.find(info => info.description === "time zone")?.name,
        }
      },
    };

    fingerprint = Object.entries(parsedData ?? {}).reduce((acc, [key, value]) => {
      if(value !== null && value !== undefined) {
        acc[key] = value;
      }
    
      return acc;
    }, {}) as Fingerprint

    if(Object.keys(fingerprint).length > 0) { 
      StorageService.setItem(storageKey, fingerprint);
    }
  } catch(error) { 
    onError?.(error);
    logger.error("Error getting browser fingerprint", error);
  }
    
  return fingerprint;
}

function getUserLocationViaGPS() {
  return new Promise((resolve, reject) => {
    if("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        // Pass these coordinates to a reverse geocoding API
        const data = await getCity(lat, lon);
        resolve(data);
      }, (error) => {
        logger.error("Error code:", error.code, "Message:", error.message);
        reject(error);
      });
    } else {
      logger.warn("Geolocation is not supported by this browser.");
      reject("Geolocation is not supported by this browser.");
    }
  });

  // See: 
  // - https://www.bigdatacloud.com/free-api/free-reverse-geocode-to-city-api
  // - https://www.bigdatacloud.com/docs/article/fair-use-policy-for-free-client-side-reverse-geocoding-api
  async function getCity(lat: number, lon: number) {
    let url = "https://api.bigdatacloud.net/data/reverse-geocode-client";
    url += `?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      
      return data;
    } catch (err) {
      logger.error("Reverse geocoding failed:", err);
      return null;
    }
  }
}

/*async function getFingerprintWithConsent(params?: GetFingerprint) {
  // Check for existing consent
  const hasConsent = localStorage.getItem("fingerprint-consent") === "true";

  // Function to request consent
  function requestConsent() {
    if(confirm("We use device fingerprinting to improve security. Allow this feature?")) {
      localStorage.setItem("fingerprint-consent", "true");
      return true;
    }

    return false;
  }

  // Usage
  if(hasConsent || requestConsent()) {
    return await getFingerprint(params);
  }
}*/

/*export function removeNullAndUndefinedFromFingerprint(fingerprint: Fingerprint | null) {
  return Object.entries(fingerprint ?? {}).reduce<Record<string, string>>((acc, [key, value]) => {
    if(value !== null && value !== undefined) {
      acc[key] = typeof value === "string" ? value : JSON.stringify(value);
    }
  
    return acc;
  }, {});
}*/
